import { track } from '@tldraw/tldraw'
import cx from 'classnames'
import { FontFamilyStyle, FontWeightStyle, ItalicStyle } from '../VisualEditor/StyleProps'
import { useUpdateStyle } from '../VisualEditor/hooks/useUpdateStyle'
import { Item } from '@react-stately/collections'
import { FontFamilyProps } from './Types'
import { ComboBox, ComboBoxProps } from 'components/ComboBox/ComboBox'
import { useState } from 'react'
import Styles from './FontFamily.module.scss'

import { useFonts } from 'hooks/queries/workspaces/useFonts'
import { FontFamilyComboBox } from './FontFamilyComboBox'
import { observer } from 'mobx-react-lite'
import { useRootStore } from 'stores/RootStore'
import { useSanitiseFontConfig } from '../VisualEditor/hooks/useSanitiseFontConfig'

export const FontFamily = track(
  observer(({ editor, style, fontWeightStyle, italicStyle, fonts = [], brandKitFontFamilies }: FontFamilyProps) => {
    const [isSearching, setIsSearching] = useState(false)
    const updateStyle = useUpdateStyle(editor)
    const { fonts: fontsStore } = useRootStore()
    const fontLoadError = style.type === 'shared' && !fontsStore.get(style.value)
    const sanitiseConfig = useSanitiseFontConfig()

    const baseProps: Partial<ComboBoxProps> = {
      'aria-label': 'Font family',
      size: 'md',
      wrapperClassName: cx(Styles.comboboxWrapper, { [Styles.error]: fontLoadError }),
      contentsClassName: Styles.comboboxContents,
      inputClassName: Styles.comboboxInput,
      popoverClassName: Styles.comboboxPopover,
    }

    if (!isSearching) {
      return (
        <div className={Styles.fontWrapper}>
          <ComboBox
            {...baseProps}
            onFocus={() => setIsSearching(true)}
            inputValue={style.type === 'shared' ? style.value : 'Mixed'}
          >
            <Item>{style.type === 'shared' ? style.value : 'Mixed'}</Item>
          </ComboBox>
          {fontLoadError && (
            <span className={Styles.errorText}>Custom font is missing. Re-upload it or choose another font.</span>
          )}
        </div>
      )
    }

    return (
      <FontFamilyComboBox
        {...baseProps}
        autoFocus
        placeholder="Search fonts..."
        menuTrigger="focus"
        onBlur={() => setIsSearching(false)}
        onSelectionChange={(newFamily) => {
          if (!newFamily) return

          const fontFamily = newFamily as string
          const weight = fontWeightStyle.type === 'mixed' ? 400 : fontWeightStyle.value
          const italic = italicStyle.type === 'mixed' ? false : italicStyle.value

          const sanitisedConfig = sanitiseConfig({ fontFamily, weight, italic })

          updateStyle(FontFamilyStyle, sanitisedConfig.fontFamily)
          updateStyle(FontWeightStyle, sanitisedConfig.weight)
          updateStyle(ItalicStyle, sanitisedConfig.italic)

          setIsSearching(false)
        }}
        selectedKey={style.type === 'shared' ? style.value : 'Mixed'}
        defaultInputValue=""
        fonts={fonts}
        popoverMaxHeight={500}
        brandKitFontFamilies={brandKitFontFamilies}
      />
    )
  }),
)

export const FontFamilyWithUploadedFonts = (props: Omit<FontFamilyProps, 'fonts'>) => {
  const { data } = useFonts({ cacheTime: 1000 * 60 * 5 })

  return <FontFamily fonts={data?.fonts} {...props} />
}
